import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faCheckCircle, faTimesCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

const UniversalConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  type = 'warning',
  title,
  message,
  confirmText,
  cancelText,
  showCancelButton = true,
  theme = 'modern', // 'modern' (jquery-confirm style) or 'tailwind'
  closeOnBackdrop = true,
  autoClose = false,
  autoCloseDelay = 3000
}) => {
  if (!isOpen) return null;

  // Auto-close functionality
  if (autoClose && onConfirm) {
    setTimeout(() => {
      onConfirm();
      onClose();
    }, autoCloseDelay);
  }

  // Configuration based on type
  const config = {
    successconfig: {
      iconColor: '#2ecc71',
      icon: faExclamationTriangle,
      borderColor: '#2ecc71',
      confirmButtonColor: '#5cb85c',
      confirmButtonHoverColor: '#4cae4c',
      defaultTitle: 'Success!',
      defaultMessage: 'Operation completed successfully.',
      defaultConfirmText: 'OK',
      defaultCancelText: 'CANCEL'
    },
    success: {
      iconColor: '#2ecc71',
      icon: faCheckCircle,
      borderColor: '#2ecc71',
      confirmButtonColor: '#5cb85c',
      confirmButtonHoverColor: '#4cae4c',
      defaultTitle: 'Success!',
      defaultMessage: 'Operation completed successfully.',
      defaultConfirmText: 'OK',
      defaultCancelText: 'CANCEL'
    },
    error: {
      iconColor: '#e74c3c',
      icon: faTimesCircle,
      borderColor: '#e74c3c',
      confirmButtonColor: '#d9534f',
      confirmButtonHoverColor: '#c9302c',
      defaultTitle: 'Error',
      defaultMessage: 'An error occurred.',
      defaultConfirmText: 'OK',
      defaultCancelText: 'CANCEL'
    },
    warning: {
      iconColor: '#ffd200',
      icon: faExclamationTriangle,
      borderColor: '#ffd200',
      confirmButtonColor: '#ffd200',
      confirmButtonHoverColor: '#ffd200',
      defaultTitle: 'Are you sure?',
      defaultMessage: 'Please confirm your action.',
      defaultConfirmText: 'YES',
      defaultCancelText: 'CANCEL'
    },
    info: {
      iconColor: '#5bc0de',
      icon: faInfoCircle,
      borderColor: '#5bc0de',
      confirmButtonColor: '#5bc0de',
      confirmButtonHoverColor: '#46b8da',
      defaultTitle: 'Information',
      defaultMessage: 'Please note the following information.',
      defaultConfirmText: 'OK',
      defaultCancelText: 'CANCEL'
    },
    delete: {
      iconColor: '#e74c3c',
      icon: faExclamationTriangle,
      borderColor: '#e74c3c',
      confirmButtonColor: '#e74c3c',
      confirmButtonHoverColor: '#c0392b',
      defaultTitle: 'Delete Message(s)?',
      defaultMessage: 'You are about to Delete one or more message(s). Click "DELETE" to proceed or "CANCEL" to exit.',
      defaultConfirmText: 'DELETE',
      defaultCancelText: 'CANCEL'
    },
    cancel: {
      iconColor: '#d9534f',
      icon: faExclamationTriangle,
      borderColor: '#d9534f',
      confirmButtonColor: '#d9534f',
      confirmButtonHoverColor: '#c9302c',
      defaultTitle: 'Are you sure?',
      defaultMessage: 'You are about to CANCEL this Flight. Click YES to proceed or CANCEL to exit.',
      defaultConfirmText: 'YES',
      defaultCancelText: 'CANCEL'
    },
    reschedule: {
      iconColor: '#2ecc71',
      icon: faCheckCircle,
      borderColor: '#2ecc71',
      confirmButtonColor: '#5cb85c',
      confirmButtonHoverColor: '#4cae4c',
      defaultTitle: 'Are you sure?',
      defaultMessage: 'You are about to RESCHEDULE this item. Click OK to proceed or CANCEL to exit.',
      defaultConfirmText: 'OK',
      defaultCancelText: 'CANCEL'
    },
    profileIncomplete: {
      iconColor: '#e74c3c',
      icon: faTimesCircle,
      borderColor: '#e74c3c',
      confirmButtonColor: '#FFEB00',
      confirmButtonHoverColor: '#FFD700',
      defaultTitle: 'You have not completed your profile',
      defaultMessage: 'Please complete your profile before proceeding.',
      defaultConfirmText: 'OK',
      defaultCancelText: 'CANCEL'
    },
    restore: {
      iconColor: '#2ecc71',
      icon: faCheckCircle,
      borderColor: '#2ecc71',
      confirmButtonColor: '#5cb85c',
      confirmButtonHoverColor: '#4cae4c',
      defaultTitle: 'Restore Flight?',
      defaultMessage: 'Are you sure you want to restore this flight? It will be available again.',
      defaultConfirmText: 'RESTORE',
      defaultCancelText: 'CANCEL'
    },
    permanentDelete: {
      iconColor: '#e74c3c',
      icon: faExclamationTriangle,
      borderColor: '#e74c3c',
      confirmButtonColor: '#d9534f',
      confirmButtonHoverColor: '#c9302c',
      defaultTitle: 'Permanently Delete Flight?',
      defaultMessage: 'Are you sure you want to permanently delete this flight? This action cannot be undone.',
      defaultConfirmText: 'DELETE PERMANENTLY',
      defaultCancelText: 'CANCEL'
    }
  };

  const currentConfig = config[type] || config.warning;

  const handleBackdropClick = (e) => {
    if (closeOnBackdrop && e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    if (onClose) {
      onClose();
    }
  };

  // Modern theme (jquery-confirm style)
  if (theme === 'modern') {
    return (
      <>
        <style>
          {`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes slideDown {
              from { transform: translateY(-50px); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }
          `}
        </style>
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] animate-[fadeIn_0.3s_ease-in-out]" 
          onClick={handleBackdropClick}
        >
          <div 
            className="bg-white rounded-lg pb-5 w-[90%] max-w-[420px] text-center relative shadow-[0_4px_20px_rgba(0,0,0,0.15)] animate-[slideDown_0.4s_ease-out] overflow-hidden" 
            onClick={(e) => e.stopPropagation()}
            style={{ borderTop: `7px solid ${currentConfig.borderColor}` }}
          >
            {/* Icon */}
            <div className="pt-[10px] pb-[10px] flex justify-center items-center relative">
              <div className="w-[82px] h-[82px] bg-white rounded-full flex items-center justify-center shadow-[0_2px_10px_rgba(0,0,0,0.15)]">
                <FontAwesomeIcon
                  icon={currentConfig.icon}
                  className="!h-[56.25px] !w-[78px]"
                  style={{ color: currentConfig.iconColor }}
                />
              </div>
            </div>

            {/* Content */}
            <div className="px-[30px] pt-0 ">
              <h3 className="text-[22px] font-semibold text-[#444] mb-[10px] leading-[1.2]">
                {title || currentConfig.defaultTitle}
              </h3>
              <p className="text-center text-[15px] text-[#777] mb-[15px] leading-[17.81px] font-normal" style={{ fontFamily: "'Bahnschrift', sans-serif" }}>
                {message || currentConfig.defaultMessage}
              </p>

              {/* Buttons */}
              <div className="flex gap-[10px] justify-center">
                <button
                  onClick={handleConfirm}
                  className="min-w-[80px] px-4 py-2 text-black text-[14px] rounded font-normal leading-[18.8px] inline-flex items-center justify-center whitespace-nowrap transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.12)] cursor-pointer border-0"
                  style={{ 
                    fontFamily: "'Bahnschrift', sans-serif",
                    backgroundColor: currentConfig.confirmButtonColor,
                    color: '#000'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = currentConfig.confirmButtonHoverColor}
                  onMouseLeave={(e) => e.target.style.backgroundColor = currentConfig.confirmButtonColor}
                >
                  {confirmText || currentConfig.defaultConfirmText}
                </button>
                {showCancelButton && (
                  <button
                    onClick={() => onClose && onClose()}
                    className="min-w-[80px] px-4 py-2 bg-white text-black text-[14px] rounded font-normal leading-[14.8px] hover:bg-gray-100 inline-flex items-center justify-center whitespace-nowrap transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.12)] cursor-pointer border-2 border-[#b9b9b9]"
                    style={{ fontFamily: "'Bahnschrift', sans-serif" }}
                  >
                    {cancelText || currentConfig.defaultCancelText}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Tailwind theme (original ConfirmationModal style)
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4" 
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full animate-fade-in">
        <div className="p-6 text-center">
          {/* Icon */}
          <div className="mb-4">
            <div 
              className="inline-flex items-center justify-center w-16 h-16 rounded-full"
              style={{ 
                backgroundColor: `${currentConfig.iconColor}20`,
                color: currentConfig.iconColor 
              }}
            >
              <FontAwesomeIcon icon={currentConfig.icon} className="text-3xl" />
            </div>
          </div>
          
          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {title || currentConfig.defaultTitle}
          </h3>
          
          {/* Message */}
          <p className="text-center text-[15px] text-[#777] mb-6 leading-[17.81px] font-normal" style={{ fontFamily: "'Bahnschrift', sans-serif" }}>
            {message || currentConfig.defaultMessage}
          </p>
          
          {/* Buttons */}
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleConfirm}
              className="min-w-[80px] px-4 py-2 text-black text-[14px] rounded font-normal leading-[18.8px] inline-flex items-center justify-center whitespace-nowrap transition-all duration-300 hover:-translate-y-0.5 focus:outline-none shadow-[0_1px_3px_rgba(0,0,0,0.12)] cursor-pointer border-0"
              style={{
                fontFamily: "'Bahnschrift', sans-serif",
                backgroundColor: currentConfig.confirmButtonColor,
                color: '#000'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = currentConfig.confirmButtonHoverColor}
              onMouseLeave={(e) => e.target.style.backgroundColor = currentConfig.confirmButtonColor}
            >
              {confirmText || currentConfig.defaultConfirmText}
            </button>
            {showCancelButton && (
              <button
                onClick={() => onClose && onClose()}
                className="min-w-[80px] px-4 py-2 bg-white text-black text-[14px] rounded font-normal leading-[14.8px] hover:bg-gray-100 inline-flex items-center justify-center whitespace-nowrap transition-all duration-300 focus:outline-none shadow-[0_1px_3px_rgba(0,0,0,0.12)] cursor-pointer border-2 border-[#b9b9b9]"
                style={{ fontFamily: "'Bahnschrift', sans-serif" }}
              >
                {cancelText || currentConfig.defaultCancelText}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UniversalConfirmModal;
